import { AxiosError } from 'axios';
import { GetServerSidePropsContext } from 'next';
import { NextSeo } from 'next-seo';
import { useEffect, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import fetchByProperties from '~/api/utils';
import { ICreatorPlatformStatusValue } from '~/clientApi';
import Error404 from '~/components/Error404';
import Error500 from '~/components/Error500';
import { MarketingBanner } from '~/components/MarketingBanner';
import { SHARED_ENTITIES } from '~/components/MarketingBanner/MarketingBanner';
import { SOCIAL_PLATFORMS } from '~/constants';
import { processOgImages } from '~/metaDataUtils';
import { ContentSharedScreen } from '~/screens/ContentSharedScreen';
import { TikTokContentType } from '~/screens/ContentSharedScreen/ContentSharedScreen.types';
import { ASSET_TYPE, IApiSearchTiktokContent } from '~/searchApi';
import { CreatorType } from '~/types';

interface Props {
  contentServer?: TikTokContentType;
  creatorServer?: CreatorType;
  isNotFound?: boolean;
}

export default function SharedLiveContentDetailPageBrand({
  contentServer,
  creatorServer,
  isNotFound,
}: Props) {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const handleError = ({ error }: { error: Error }) => {
    if (error.message.match(/404/)) {
      return <Error404 />;
    }
    return <Error500 />;
  };

  // The link points at something that is no longer shared. Say so, instead of
  // rendering a half-empty page or falling through to “Something went wrong…”.
  if (isNotFound || !contentServer || !creatorServer) {
    return <Error404 />;
  }

  const content: IApiSearchTiktokContent = {
    ...contentServer,
    platform: SOCIAL_PLATFORMS.tiktok,
    type: ASSET_TYPE.video,
    contentId: contentServer.id,
    createdTime: contentServer.createTime,
    creator: {
      // `tiktok-contents` no longer returns the deprecated `creator` relation,
      // so the id comes from the `creators` call the share token also answers.
      id: creatorServer.id,
      status: creatorServer.status as ICreatorPlatformStatusValue,
      platforms: {},
    },
    engagements: contentServer.engagementCount,
    publishedAt: contentServer.createdAt,
    views: contentServer.videoViews,
  };

  return (
    <ErrorBoundary fallbackRender={handleError}>
      <NextSeo
        title="Share Live Content"
        openGraph={{
          title: creatorServer.fullName,
          description: 'TikTok content',
          type: 'website',
          images: processOgImages(
            [contentServer.assets[0].previewUrl as string],
            'content',
          ),
        }}
      />

      {isClient && (
        <>
          <ContentSharedScreen
            content={{ ...content, shareLink: window.location.href }}
            creator={{
              avatar: creatorServer.avatar as string,
              fullName: creatorServer.fullName as string,
              slug: creatorServer.slug as string,
              status: creatorServer.status as ICreatorPlatformStatusValue,
            }}
          />
          <MarketingBanner
            hide={!creatorServer?.agency}
            agencyName={creatorServer?.agency?.name}
            entity={SHARED_ENTITIES.content}
            trackId="foam-marketing-banner"
          />
        </>
      )}
    </ErrorBoundary>
  );
}

/**
 * Next's own `notFound: true` renders `pages/404` without `isSSR`, which leaves
 * `AuthProvider` spinning forever for an anonymous visitor. So the page renders
 * `Error404` itself and only the status code is set here.
 */
function notFoundProps(context: GetServerSidePropsContext) {
  context.res.statusCode = 404;

  return { props: { isSSR: true, isNotFound: true } };
}

export async function getServerSideProps(context: GetServerSidePropsContext) {
  const { sharedToken } = context.params as { sharedToken: string };

  try {
    const content = await fetchByProperties('tiktok-contents', sharedToken, []);
    const creator = await fetchByProperties(`creators`, sharedToken, [
      'id',
      'fullName',
      'avatar',
      'slug',
      'agency',
    ]);

    const contentServer = content.results?.[0];
    const creatorServer = creator.results?.[0];

    if (!contentServer || !creatorServer) {
      return notFoundProps(context);
    }

    return {
      props: {
        isSSR: true,
        contentServer,
        creatorServer,
      },
    };
  } catch (error) {
    // A revoked or expired share token answers 404: the link is gone, which is
    // not a failure of ours. Anything else is, so let it reach the 500 page.
    if ((error as AxiosError)?.response?.status === 404) {
      return notFoundProps(context);
    }
    throw error;
  }
}
